import React, { Fragment, useState, useEffect, useMemo } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  IconX,
  IconCheck,
  IconTrash,
  IconAlertCircle,
  IconPrinter,
  IconChartPie,
  IconListDetails,
  IconPhoto,
  IconSearch,
  IconInbox
} from '@tabler/icons-react';
import axios from 'axios';
import moment from 'moment';
import { PhotoProvider, PhotoView } from 'react-photo-view';
import 'react-photo-view/dist/react-photo-view.css';
import { toast } from 'react-toastify';
import useCabang from '../../pages/features/useCabang';
import { imageBaseUrl } from '../../config/http';
import SuratJalanPrint from '../SuratJalanPrint';

export default function ModalBuatPengirimanCepat({
  isOpen,
  onClose,
  selectedItems = [],
  setSelectedItems,
  onSuccess
}) {
  const { cabang } = useCabang();
  const token = localStorage.getItem('token');

  const [cabangTujuanId, setCabangTujuanId] = useState('');
  const [namaKurir, setNamaKurir] = useState('');
  const [catatanPengiriman, setCatatanPengiriman] = useState('');
  const [itemsToShip, setItemsToShip] = useState([]);
  const [modalTab, setModalTab] = useState('ringkasan');
  const [searchModalItem, setSearchModalItem] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittingType, setSubmittingType] = useState('standar');

  // Print state
  const [isOpenPrint, setIsOpenPrint] = useState(false);
  const [createdData, setCreatedData] = useState(null);
  const [createdBreakdown, setCreatedBreakdown] = useState([]);

  // Sync selected items when modal opens
  useEffect(() => {
    if (isOpen) {
      setItemsToShip(selectedItems);
      setModalTab('ringkasan');
      setSearchModalItem('');
    }
  }, [isOpen, selectedItems]);

  // Hanya cabang yang berstatus Etalase (bukan Brankas)
  const tokoList = (cabang || []).filter(
    (c) =>
      (c.is_toko === true || c.is_toko === 1 || c.is_toko === '1') &&
      !c.nama_cabang?.toLowerCase().includes('brankas')
  );

  const totalPcs = itemsToShip.length;
  const totalBerat = itemsToShip.reduce(
    (sum, it) => sum + parseFloat(it.berat || it.item?.berat || 0),
    0
  );

  // Grouped summary by Category & Karat
  const detailSummary = useMemo(() => {
    return itemsToShip.reduce((acc, item) => {
      const catName =
        item.jenis_kategori?.kategori?.nama_kategori ||
        item.kategori?.nama_kategori ||
        item.jenis_kategori?.nama_jenis ||
        'Tanpa Kategori';
      const karat = item.karat || '-';
      const berat = parseFloat(item.berat || item.item?.berat || 0);

      if (!acc[catName]) {
        acc[catName] = { name: catName, pcs: 0, berat: 0, byKarat: {} };
      }

      acc[catName].pcs += 1;
      acc[catName].berat += berat;

      if (!acc[catName].byKarat[karat]) {
        acc[catName].byKarat[karat] = { pcs: 0, berat: 0 };
      }

      acc[catName].byKarat[karat].pcs += 1;
      acc[catName].byKarat[karat].berat += berat;

      return acc;
    }, {});
  }, [itemsToShip]);

  // Image resolver helper
  const resolveItemImageUrl = (item) => {
    if (!item) return '';
    let gData = item.gambar1 || item.gambar;
    if (typeof gData === 'string' && (gData.startsWith('[') || gData.startsWith('{'))) {
      try {
        gData = JSON.parse(gData);
      } catch (e) {}
    }
    if (Array.isArray(gData) && gData.length > 0) {
      const path =
        gData[0]?.asli ||
        gData[0]?.thumbnail ||
        (typeof gData[0] === 'string' ? gData[0] : '');
      return path ? (path.startsWith('http') ? path : `${imageBaseUrl}${path}`) : '';
    }
    if (typeof gData === 'string' && gData.length > 0) {
      return gData.startsWith('http') ? gData : `${imageBaseUrl}${gData}`;
    }
    return '';
  };

  // Filtered items inside Detail Modal search
  const filteredModalItems = useMemo(() => {
    if (!itemsToShip || itemsToShip.length === 0) return [];
    if (!searchModalItem.trim()) return itemsToShip;
    const q = searchModalItem.toLowerCase();
    return itemsToShip.filter((it) => {
      const kode = (it.kode_item || it.kode || '').toLowerCase();
      const jenis = (it.jenis_kategori?.nama_jenis || '').toLowerCase();
      const kat = (
        it.jenis_kategori?.kategori?.nama_kategori ||
        it.kategori?.nama_kategori ||
        ''
      ).toLowerCase();
      const baki = (it.nomor_rak || it.baki || '').toString().toLowerCase();
      return kode.includes(q) || jenis.includes(q) || kat.includes(q) || baki.includes(q);
    });
  }, [itemsToShip, searchModalItem]);

  const handleRemoveItem = (id) => {
    const updated = itemsToShip.filter((it) => it.id !== id);
    setItemsToShip(updated);
    if (setSelectedItems) {
      setSelectedItems(updated);
    }
  };

  const isFormValid = Boolean(cabangTujuanId && namaKurir.trim() && itemsToShip.length > 0);

  const handleSubmit = async (tanpaVerifikasi = false) => {
    if (!cabangTujuanId) {
      toast.warning('Silakan pilih Toko Tujuan pengiriman!');
      return;
    }
    if (!namaKurir.trim()) {
      toast.warning('Silakan isi nama kurir / pengantar barang!');
      return;
    }
    if (itemsToShip.length === 0) {
      toast.warning('Pilih minimal 1 barang dari Brankas untuk dikirim!');
      return;
    }

    setIsSubmitting(true);
    setSubmittingType(tanpaVerifikasi ? 'tanpa_verifikasi' : 'standar');

    try {
      const payload = {
        cabang_asal_id: 4,
        cabang_tujuan_id: cabangTujuanId,
        nama_kurir: namaKurir.trim(),
        items: itemsToShip.map((i) => i.id),
        catatan_pengiriman: catatanPengiriman.trim(),
        tanpa_verifikasi: tanpaVerifikasi
      };

      const res = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/pengiriman`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data?.status) {
        let detailData = res.data.data;
        let breakdown = res.data.breakdown_kategori || [];

        // Fallback jika breakdown belum tersedia
        if (!breakdown || breakdown.length === 0) {
          try {
            const detailRes = await axios.get(
              `${process.env.REACT_APP_BASE_URL}/pengiriman/${res.data.data.id}`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            detailData = detailRes.data.data;
            breakdown = detailRes.data.breakdown_kategori || [];
          } catch (e) {
            console.warn('Fallback detail fetch error:', e);
          }
        }

        toast.success(
          tanpaVerifikasi
            ? `Surat Jalan ${res.data.data.no_surat_jalan} berhasil dibuat & langsung masuk etalase!`
            : `Surat Jalan ${res.data.data.no_surat_jalan} berhasil dibuat!`
        );

        setCreatedData(detailData);
        setCreatedBreakdown(breakdown);
        setIsOpenPrint(true);

        // Reset form
        setNamaKurir('');
        setCatatanPengiriman('');
        setCabangTujuanId('');
        setItemsToShip([]);
        if (setSelectedItems) setSelectedItems([]);

        if (onSuccess) onSuccess();
      }
    } catch (err) {
      console.error('Gagal membuat surat jalan:', err);
      toast.error(err.response?.data?.message || 'Gagal memproses pengiriman');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClosePrintModal = () => {
    setIsOpenPrint(false);
    onClose();
  };

  // Stat calculations for Tab Ringkasan
  const totalPcsVal = totalPcs;
  const totalBeratVal = totalBerat;
  const avgBeratVal = totalPcsVal > 0 ? totalBeratVal / totalPcsVal : 0;
  const validModalItems = itemsToShip.filter(
    (it) => Number(it.harga_modal || it.item?.harga_modal || 0) > 0
  );
  const avgModalVal =
    validModalItems.length > 0
      ? Math.round(
          validModalItems.reduce(
            (acc, it) => acc + Number(it.harga_modal || it.item?.harga_modal || 0),
            0
          ) / validModalItems.length
        )
      : 0;
  const validWeights = itemsToShip
    .map((it) => parseFloat(it.berat || it.item?.berat || 0) || 0)
    .filter((w) => w > 0);
  const minBeratVal = validWeights.length > 0 ? Math.min(...validWeights) : 0;
  const maxBeratVal =
    itemsToShip.length > 0
      ? Math.max(
          ...itemsToShip.map((it) => parseFloat(it.berat || it.item?.berat || 0) || 0)
        )
      : 0;
  const minModalVal =
    validModalItems.length > 0
      ? Math.min(
          ...validModalItems.map((it) => Number(it.harga_modal || it.item?.harga_modal || 0) || 0)
        )
      : 0;
  const maxModalVal =
    itemsToShip.length > 0
      ? Math.max(
          ...itemsToShip.map((it) => Number(it.harga_modal || it.item?.harga_modal || 0) || 0)
        )
      : 0;

  return (
    <>
      {isOpenPrint && (
        <SuratJalanPrint
          isOpen={isOpenPrint}
          closeModal={handleClosePrintModal}
          data={createdData}
          breakdown={createdBreakdown}
        />
      )}

      <Transition appear show={isOpen && !isOpenPrint} as={Fragment}>
        <Dialog as="div" className="relative z-[9999]" onClose={onClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-3 sm:p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-4xl lg:max-w-5xl transform overflow-hidden rounded-2xl bg-white dark:bg-slate-800 text-left align-middle shadow-2xl transition-all border border-slate-200 dark:border-slate-700 flex flex-col max-h-[92vh] space-y-3.5 p-4 sm:p-5">
                  
                  {/* Modal Header */}
                  <div className="flex items-start justify-between border-b border-slate-100 dark:border-slate-700 pb-3 flex-shrink-0">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Dialog.Title as="h3" className="font-bold text-sm sm:text-base text-slate-800 dark:text-slate-100">
                          Detail Barang Dikeluarkan: <span className="font-mono text-amber-600">DRAFT-PENGIRIMAN</span>
                        </Dialog.Title>
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300 border border-amber-300 dark:border-amber-700 text-xs font-semibold">
                          ● Siap Dikirim
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-500 mt-1">
                        <span>
                          <strong className="text-slate-600 dark:text-slate-400">Tanggal:</strong> {moment().format('DD MMM YYYY, HH:mm')}
                        </span>
                        <span>
                          <strong className="text-slate-600 dark:text-slate-400">Asal:</strong> Brankas Toko Pusat (Rumah)
                        </span>
                        {cabangTujuanId && (
                          <span>
                            <strong className="text-slate-600 dark:text-slate-400">Tujuan:</strong>{' '}
                            {cabang.find((c) => String(c.id) === String(cabangTujuanId))?.nama_cabang || '-'}
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={onClose}
                      className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors cursor-pointer"
                    >
                      <IconX size={20} />
                    </button>
                  </div>

                  {/* Top Quick Info Strip */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs bg-slate-50 dark:bg-slate-700/40 p-3 sm:p-3.5 rounded-xl border border-slate-100 dark:border-slate-700/60 flex-shrink-0">
                    <div>
                      <span className="text-slate-400 dark:text-slate-400 block text-[10px] uppercase font-bold tracking-wider mb-1">
                        Toko Asal (Brankas)
                      </span>
                      <span className="font-bold text-slate-800 dark:text-slate-200 truncate block text-xs pt-1">
                        Brankas Toko Pusat (Rumah)
                      </span>
                    </div>

                    <div>
                      <label className="text-slate-500 dark:text-slate-400 block text-[10px] uppercase font-bold tracking-wider mb-1">
                        Toko Tujuan <span className="text-rose-500">*</span>
                      </label>
                      <select
                        value={cabangTujuanId}
                        onChange={(e) => setCabangTujuanId(e.target.value)}
                        className="w-full px-2.5 py-1 text-xs font-semibold rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 text-slate-800 dark:text-slate-100 focus:ring-1 focus:ring-amber-500 outline-none cursor-pointer"
                      >
                        <option value="">-- Pilih Toko Tujuan --</option>
                        {tokoList.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.nama_cabang}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <span className="text-slate-400 dark:text-slate-400 block text-[10px] uppercase font-bold tracking-wider mb-1">
                        Total Item & Berat
                      </span>
                      <span className="font-bold text-slate-800 dark:text-slate-200 truncate block text-xs pt-1">
                        {totalPcs} PCS{' '}
                        <span className="text-amber-600 dark:text-amber-400 font-semibold font-mono">
                          ({totalBerat.toFixed(3)} gr)
                        </span>
                      </span>
                    </div>

                    <div>
                      <label className="text-slate-500 dark:text-slate-400 block text-[10px] uppercase font-bold tracking-wider mb-1">
                        Kurir Pengantar <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="text"
                        placeholder="Nama kurir..."
                        value={namaKurir}
                        onChange={(e) => setNamaKurir(e.target.value)}
                        className="w-full px-2.5 py-1 text-xs font-semibold rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 text-slate-800 dark:text-slate-100 focus:ring-1 focus:ring-amber-500 outline-none"
                      />
                    </div>
                  </div>

                  {/* Tab Navigation Bar */}
                  <div className="flex border-b border-slate-200 dark:border-slate-700 gap-1 overflow-x-auto scrollbar-none flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => setModalTab('ringkasan')}
                      className={`px-3 py-2 text-xs font-bold flex items-center gap-1.5 border-b-2 transition-all whitespace-nowrap cursor-pointer ${
                        modalTab === 'ringkasan'
                          ? 'border-amber-500 text-amber-600 dark:text-amber-400 bg-amber-50/50 dark:bg-amber-900/10'
                          : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                      }`}
                    >
                      <IconChartPie size={16} />
                      <span>Ringkasan Item</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setModalTab('list')}
                      className={`px-3 py-2 text-xs font-bold flex items-center gap-1.5 border-b-2 transition-all whitespace-nowrap cursor-pointer ${
                        modalTab === 'list'
                          ? 'border-amber-500 text-amber-600 dark:text-amber-400 bg-amber-50/50 dark:bg-amber-900/10'
                          : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                      }`}
                    >
                      <IconListDetails size={16} />
                      <span>List Item ({totalPcs})</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setModalTab('galeri')}
                      className={`px-3 py-2 text-xs font-bold flex items-center gap-1.5 border-b-2 transition-all whitespace-nowrap cursor-pointer ${
                        modalTab === 'galeri'
                          ? 'border-amber-500 text-amber-600 dark:text-amber-400 bg-amber-50/50 dark:bg-amber-900/10'
                          : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                      }`}
                    >
                      <IconPhoto size={16} />
                      <span>Galeri List Item</span>
                    </button>
                  </div>

                  {/* Scrollable Tab Content */}
                  <div className="flex-1 overflow-y-auto pr-1">
                    {itemsToShip.length === 0 ? (
                      <div className="p-8 text-center text-slate-400 dark:text-slate-500 space-y-2">
                        <IconAlertCircle size={32} className="mx-auto text-amber-500 opacity-80" />
                        <p className="text-xs font-medium">Belum ada barang yang dipilih dari List Brankas.</p>
                        <p className="text-[11px] text-slate-400">
                          Centang barang pada tabel List Brankas terlebih dahulu.
                        </p>
                      </div>
                    ) : (
                      <>
                        {/* 1. Tab Ringkasan Item */}
                        {modalTab === 'ringkasan' && (
                          <div className="space-y-4">
                            {/* Ringkasan Cards (9 Cards) */}
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-5 gap-2.5 sm:gap-3">
                              <div className="bg-amber-50/60 dark:bg-amber-950/20 border border-amber-200/70 dark:border-amber-900/40 rounded-xl p-3">
                                <span className="text-amber-700 dark:text-amber-400 text-[10px] font-bold uppercase tracking-wider block">
                                  Total Barang
                                </span>
                                <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5">
                                  {totalPcsVal} <span className="text-xs font-semibold text-slate-500">Pcs</span>
                                </div>
                              </div>
                              <div className="bg-amber-50/60 dark:bg-amber-950/20 border border-amber-200/70 dark:border-amber-900/40 rounded-xl p-3">
                                <span className="text-amber-700 dark:text-amber-400 text-[10px] font-bold uppercase tracking-wider block">
                                  Total Berat
                                </span>
                                <div className="text-lg font-extrabold text-amber-600 dark:text-amber-400 mt-0.5 font-mono">
                                  {totalBeratVal.toFixed(3)}{' '}
                                  <span className="text-xs font-semibold text-slate-500 font-sans">gr</span>
                                </div>
                              </div>
                              <div className="bg-slate-50 dark:bg-slate-700/30 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                                <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider block">
                                  Total Kategori
                                </span>
                                <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5">
                                  {Object.keys(detailSummary).length}{' '}
                                  <span className="text-xs font-semibold text-slate-500">Kategori</span>
                                </div>
                              </div>
                              <div className="bg-slate-50 dark:bg-slate-700/30 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                                <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider block">
                                  Rata-rata Berat
                                </span>
                                <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5 font-mono">
                                  {avgBeratVal.toFixed(3)}{' '}
                                  <span className="text-xs font-semibold text-slate-500 font-sans">gr</span>
                                </div>
                              </div>
                              <div className="bg-slate-50 dark:bg-slate-700/30 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                                <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider block">
                                  Berat Minimal
                                </span>
                                <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5 font-mono">
                                  {minBeratVal.toFixed(3)}{' '}
                                  <span className="text-xs font-semibold text-slate-500 font-sans">gr</span>
                                </div>
                              </div>
                              <div className="bg-slate-50 dark:bg-slate-700/30 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                                <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider block">
                                  Berat Maksimal
                                </span>
                                <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5 font-mono">
                                  {maxBeratVal.toFixed(3)}{' '}
                                  <span className="text-xs font-semibold text-slate-500 font-sans">gr</span>
                                </div>
                              </div>
                              <div className="bg-slate-50 dark:bg-slate-700/30 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                                <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider block">
                                  Rata-rata Modal
                                </span>
                                <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5">
                                  Rp {avgModalVal.toLocaleString('id-ID')}
                                </div>
                              </div>
                              <div className="bg-slate-50 dark:bg-slate-700/30 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                                <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider block">
                                  Modal Minimal
                                </span>
                                <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5">
                                  Rp {minModalVal.toLocaleString('id-ID')}
                                </div>
                              </div>
                              <div className="bg-slate-50 dark:bg-slate-700/30 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                                <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider block">
                                  Modal Maksimal
                                </span>
                                <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5">
                                  Rp {maxModalVal.toLocaleString('id-ID')}
                                </div>
                              </div>
                            </div>

                            {/* Breakdown Kategori & Karat */}
                            {Object.keys(detailSummary).length > 0 && (
                              <div className="space-y-3 p-3.5 bg-amber-50/50 dark:bg-amber-900/10 border border-amber-200/50 dark:border-amber-900/30 rounded-xl">
                                <div className="flex items-center justify-between">
                                  <h4 className="text-[11px] font-bold text-amber-800 dark:text-amber-400 uppercase tracking-wider flex items-center gap-1.5">
                                    <IconChartPie size={16} />
                                    <span>Breakdown Per Kategori & Karat</span>
                                  </h4>
                                  <span className="text-[10px] text-amber-700 dark:text-amber-400 font-semibold">
                                    {Object.keys(detailSummary).length} Kategori
                                  </span>
                                </div>
                                <div className="space-y-2">
                                  {Object.values(detailSummary).map((cat, idx) => (
                                    <div
                                      key={idx}
                                      className="bg-white dark:bg-slate-800 p-3 rounded-xl border border-amber-200/60 dark:border-slate-700 shadow-xs"
                                    >
                                      <div className="flex justify-between items-center mb-2 pb-2 border-b border-slate-100 dark:border-slate-700">
                                        <span className="font-bold text-slate-800 dark:text-slate-100 text-sm">
                                          {cat.name}
                                        </span>
                                        <span className="font-bold text-amber-600 dark:text-amber-400 text-xs bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 px-2.5 py-0.5 rounded-md">
                                          {cat.pcs} Pcs ({cat.berat.toFixed(3)} gr)
                                        </span>
                                      </div>
                                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                                        {Object.entries(cat.byKarat).map(([karat, val], i) => (
                                          <div
                                            key={i}
                                            className="flex items-center justify-between bg-slate-50 dark:bg-slate-700/40 px-2.5 py-1.5 rounded-lg text-xs border border-slate-100 dark:border-slate-700"
                                          >
                                            <span className="font-semibold text-slate-500 dark:text-slate-400">
                                              {karat} Karat
                                            </span>
                                            <span className="font-bold text-slate-800 dark:text-slate-100">
                                              {val.pcs} Pcs / {val.berat.toFixed(3)}g
                                            </span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Catatan Area */}
                            <div className="p-3 bg-slate-50 dark:bg-slate-700/30 rounded-xl border border-slate-200 dark:border-slate-700 space-y-1.5">
                              <span className="text-slate-400 dark:text-slate-400 text-[10px] font-bold uppercase tracking-wider block">
                                Catatan Pengiriman (Opsional)
                              </span>
                              <input
                                type="text"
                                placeholder="Tulis catatan pengiriman jika ada (contoh: titip baki cincin etalase depan)..."
                                value={catatanPengiriman}
                                onChange={(e) => setCatatanPengiriman(e.target.value)}
                                className="w-full px-3 py-1.5 text-xs bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-800 dark:text-slate-100 outline-none focus:ring-1 focus:ring-amber-500"
                              />
                            </div>
                          </div>
                        )}

                        {/* 2. Tab List Item (Tabel) */}
                        {modalTab === 'list' && (
                          <div className="space-y-3">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                              <div className="relative flex-1 max-w-xs">
                                <IconSearch
                                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                                  size={15}
                                />
                                <input
                                  type="text"
                                  placeholder="Cari kode, jenis, baki..."
                                  value={searchModalItem}
                                  onChange={(e) => setSearchModalItem(e.target.value)}
                                  className="w-full pl-8 pr-3 py-1.5 text-xs bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 rounded-lg outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-200"
                                />
                              </div>
                              <span className="text-[11px] text-slate-500 font-medium">
                                Menampilkan {filteredModalItems.length} dari {totalPcs} item terpilih
                              </span>
                            </div>

                            <div className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-x-auto max-h-80">
                              <table className="w-full text-xs text-left text-slate-600 dark:text-slate-300">
                                <thead className="bg-gradient-to-r from-amber-50 via-amber-100/80 to-amber-50 dark:from-amber-900/40 dark:via-amber-800/30 dark:to-amber-900/40 text-[11px] text-amber-800 dark:text-amber-300 uppercase border-b border-amber-200/70 dark:border-amber-800/40 sticky top-0 z-10 font-bold tracking-wider">
                                  <tr>
                                    <th className="p-2.5 w-10 text-center font-bold">No</th>
                                    <th className="p-2.5 font-bold">Kode Barang</th>
                                    <th className="p-2.5 font-bold">Nama Barang / Jenis</th>
                                    <th className="p-2.5 text-center font-bold">Baki / Rak</th>
                                    <th className="p-2.5 text-right font-bold">Berat (gr)</th>
                                    <th className="p-2.5 text-center font-bold">Karat</th>
                                    <th className="p-2.5 text-center font-bold">Aksi</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50">
                                  {filteredModalItems.length === 0 ? (
                                    <tr>
                                      <td colSpan={7} className="text-center py-6 text-slate-400">
                                        Tidak ada item yang sesuai dengan pencarian
                                      </td>
                                    </tr>
                                  ) : (
                                    filteredModalItems.map((it, idx) => (
                                      <tr
                                        key={it.id || idx}
                                        className="hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors"
                                      >
                                        <td className="p-2.5 text-slate-500 font-medium text-center">
                                          {idx + 1}.
                                        </td>
                                        <td className="p-2.5 font-mono font-bold text-amber-600 dark:text-amber-400">
                                          {it.kode_item || it.kode || '-'}
                                        </td>
                                        <td className="p-2.5">
                                          <span className="font-semibold text-slate-700 dark:text-slate-200 block">
                                            {it.jenis_kategori?.kategori?.nama_kategori ||
                                              it.kategori?.nama_kategori ||
                                              '-'}{' '}
                                            - {it.jenis_kategori?.nama_jenis || it.nama_barang || '-'}
                                          </span>
                                        </td>
                                        <td className="p-2.5 text-center font-medium">
                                          {it.nomor_rak || it.baki || '-'}
                                        </td>
                                        <td className="p-2.5 text-right font-bold text-amber-600 dark:text-amber-400">
                                          {parseFloat(it.berat || it.item?.berat || 0).toFixed(3)} gr
                                        </td>
                                        <td className="p-2.5 text-center font-semibold">
                                          {it.karat} K
                                        </td>
                                        <td className="p-2.5 text-center">
                                          <button
                                            type="button"
                                            onClick={() => handleRemoveItem(it.id)}
                                            className="p-1 text-rose-500 hover:text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-900/30 rounded-md transition-colors cursor-pointer"
                                            title="Hapus dari daftar kirim"
                                          >
                                            <IconTrash size={15} />
                                          </button>
                                        </td>
                                      </tr>
                                    ))
                                  )}
                                </tbody>
                                {filteredModalItems.length > 0 && (
                                  <tfoot className="bg-slate-50 dark:bg-slate-700/50 font-bold border-t border-slate-200 dark:border-slate-700 sticky bottom-0">
                                    <tr>
                                      <td colSpan={4} className="p-2.5 text-right text-slate-600 dark:text-slate-300">
                                        Total ({filteredModalItems.length} item):
                                      </td>
                                      <td className="p-2.5 text-right text-amber-600 dark:text-amber-400 font-mono">
                                        {filteredModalItems
                                          .reduce(
                                            (acc, it) =>
                                              acc + parseFloat(it.berat || it.item?.berat || 0),
                                            0
                                          )
                                          .toFixed(3)}{' '}
                                        gr
                                      </td>
                                      <td colSpan={2}></td>
                                    </tr>
                                  </tfoot>
                                )}
                              </table>
                            </div>
                          </div>
                        )}

                        {/* 3. Tab Galeri List Item */}
                        {modalTab === 'galeri' && (
                          <div className="space-y-4">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                              <div className="relative flex-1 max-w-xs">
                                <IconSearch
                                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                                  size={15}
                                />
                                <input
                                  type="text"
                                  placeholder="Cari kode, jenis, baki..."
                                  value={searchModalItem}
                                  onChange={(e) => setSearchModalItem(e.target.value)}
                                  className="w-full pl-8 pr-3 py-1.5 text-xs bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 rounded-lg outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-200"
                                />
                              </div>
                              <span className="text-[11px] text-slate-500 font-medium">
                                Menampilkan {filteredModalItems.length} foto item
                              </span>
                            </div>

                            {filteredModalItems.length === 0 ? (
                              <div className="py-12 text-center text-slate-400">
                                <IconInbox size={36} className="mx-auto mb-2 opacity-50" />
                                <p className="text-xs">Tidak ada item yang sesuai dengan pencarian</p>
                              </div>
                            ) : (
                              <PhotoProvider>
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                                  {filteredModalItems.map((item) => {
                                    const imgUrl = resolveItemImageUrl(item);
                                    return (
                                      <div
                                        key={item.id}
                                        className="bg-white dark:bg-slate-800 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 hover:shadow-md transition-shadow group flex flex-col"
                                      >
                                        <div className="aspect-square bg-slate-100 dark:bg-slate-700/50 relative flex items-center justify-center overflow-hidden">
                                          {imgUrl ? (
                                            <PhotoView src={imgUrl}>
                                              <img
                                                src={imgUrl}
                                                alt={item.kode_item}
                                                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105 cursor-pointer"
                                                onError={(e) => {
                                                  e.target.style.display = 'none';
                                                }}
                                              />
                                            </PhotoView>
                                          ) : (
                                            <div className="flex flex-col items-center justify-center text-slate-400 p-2 text-center">
                                              <IconPhoto size={28} className="opacity-40 mb-1" />
                                              <span className="text-[10px]">Belum Ada Gambar</span>
                                            </div>
                                          )}
                                          <div className="absolute top-2 left-2 bg-slate-900/80 backdrop-blur-xs text-amber-400 text-[10px] font-mono font-bold px-2 py-0.5 rounded shadow">
                                            {item.kode_item || item.kode || '-'}
                                          </div>
                                          <div className="absolute top-2 right-2 bg-amber-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded shadow">
                                            {item.karat} K
                                          </div>
                                        </div>

                                        <div className="p-2.5 text-xs flex-1 flex flex-col justify-between space-y-2">
                                          <div>
                                            <p
                                              className="font-bold text-slate-800 dark:text-slate-100 text-[11px] truncate"
                                              title={`${item.jenis_kategori?.kategori?.nama_kategori || ''} - ${item.jenis_kategori?.nama_jenis || ''}`}
                                            >
                                              {item.jenis_kategori?.kategori?.nama_kategori ||
                                                item.kategori?.nama_kategori ||
                                                '-'}{' '}
                                              - {item.jenis_kategori?.nama_jenis || item.nama_barang || '-'}
                                            </p>
                                            <p className="text-[10px] text-slate-500 dark:text-slate-400">
                                              Baki:{' '}
                                              <span className="font-semibold text-slate-700 dark:text-slate-300">
                                                {item.nomor_rak || item.baki || '-'}
                                              </span>
                                            </p>
                                          </div>
                                          <div className="flex items-center justify-between pt-1.5 border-t border-slate-100 dark:border-slate-700">
                                            <span className="text-[10px] text-slate-400">Berat</span>
                                            <span className="font-bold text-amber-600 dark:text-amber-400 text-xs">
                                              {parseFloat(item.berat || item.item?.berat || 0).toFixed(3)} gr
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </PhotoProvider>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Modal Footer Actions */}
                  <div className="pt-3 border-t border-slate-100 dark:border-slate-700 flex flex-col sm:flex-row items-center justify-between gap-2.5 flex-shrink-0">
                    <button
                      type="button"
                      onClick={onClose}
                      disabled={isSubmitting}
                      className="w-full sm:w-auto px-4 py-2 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl transition cursor-pointer"
                    >
                      Batal
                    </button>

                    <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
                      {/* Standar: Kirim dengan verifikasi toko */}
                      <button
                        type="button"
                        onClick={() => handleSubmit(false)}
                        disabled={!isFormValid || isSubmitting}
                        className={`flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-3.5 py-2 text-xs font-bold rounded-xl transition ${
                          !isFormValid || isSubmitting
                            ? 'bg-slate-200 dark:bg-slate-700 text-slate-400 dark:text-slate-500 cursor-not-allowed opacity-60'
                            : 'bg-slate-700 hover:bg-slate-800 text-white shadow-xs cursor-pointer'
                        }`}
                        title={
                          !isFormValid
                            ? 'Pilih Toko Tujuan (Etalase) dan isi Nama Kurir terlebih dahulu'
                            : 'Kirim dan cetak Surat Jalan'
                        }
                      >
                        {isSubmitting && submittingType === 'standar' ? (
                          <span>Memproses...</span>
                        ) : (
                          <>
                            <IconPrinter size={15} />
                            <span>Kirim & Cetak SJ</span>
                          </>
                        )}
                      </button>

                      {/* Owner / Admin Bypass: Langsung masuk etalase toko */}
                      <button
                        type="button"
                        onClick={() => handleSubmit(true)}
                        disabled={!isFormValid || isSubmitting}
                        className={`flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-4 py-2 text-xs font-bold rounded-xl transition ${
                          !isFormValid || isSubmitting
                            ? 'bg-slate-200 dark:bg-slate-700 text-slate-400 dark:text-slate-500 cursor-not-allowed opacity-60'
                            : 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white shadow-md shadow-amber-500/20 cursor-pointer'
                        }`}
                        title={
                          !isFormValid
                            ? 'Pilih Toko Tujuan (Etalase) dan isi Nama Kurir terlebih dahulu'
                            : 'Kirim dan langsung masukkan ke etalase toko tujuan tanpa menunggu konfirmasi kasir/operator toko'
                        }
                      >
                        {isSubmitting && submittingType === 'tanpa_verifikasi' ? (
                          <span>Memproses...</span>
                        ) : (
                          <>
                            <IconCheck size={16} stroke={3} />
                            <span>Langsung Masuk Etalase</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>

                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}
